import { FC, useEffect, useMemo, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { ProNotification, ProUpload } from '../../antd';
import Helper from '../../Helper';
import classNames from 'classnames';
import { getExtraData } from '../utils';
import { getTimeRandom } from '../../utils/dataUtils';
import { AliyunUploadImagesProps, EXPIRE_TIME } from '../interface';
import {
  UploadFile,
  UploadChangeParam,
  RcFile,
} from 'antd/lib/upload/interface';
import { IMAGE_THUMB_NAME } from '../../constants/constant';

const AliUploadImages: FC<AliyunUploadImagesProps> = props => {
  const {
    OSSData,
    scene,
    uploadIcon,
    uploadText,
    disabled,
    fileList,
    maxCount,
    showUploadList,
    className,
    size,
    onChange,
    onReset,
    beforeUpload,
    getPopupContainer,
    ...restProps
  } = props;
  const [currentFileList, setCurrentFileList] = useState<UploadFile<any>[]>();
  useEffect(() => {
    if (fileList)
      setCurrentFileList(Array.isArray(fileList) ? fileList : undefined);
  }, [fileList]);
  /** 上传前数据处理 */
  const handleBeforeUpload = async (file: any, FileList: RcFile[]) => {
    if (beforeUpload) {
      const beforeData = beforeUpload(file, FileList);
      if (beforeData === false || beforeData === ProUpload.LIST_IGNORE)
        return ProUpload.LIST_IGNORE;
    }
    const { dir, endpoint, expire } = OSSData;
    const nowTime = Date.now() + EXPIRE_TIME;
    const expireTime = Number(expire) * 1000;
    if (expireTime < nowTime) {
      scene && window.sessionStorage.setItem(scene, '');
      await onReset?.();
    }
    const suffix = file.name.slice(file.name.lastIndexOf('.'));
    const randomFlag = getTimeRandom();
    const filename = `${randomFlag}${suffix}`;
    const url: string = dir + filename;
    file.relativePath = `/${url}`;
    file.thumbUrl = `${endpoint}/${dir}${randomFlag}${IMAGE_THUMB_NAME}${suffix}`;
    file.url = `${endpoint}/${url}`;
    return file;
  };
  /** 获取参数 */
  const handleExtraData = (file: UploadFile<any>) => {
    return getExtraData(file, OSSData);
  };
  /** 上传文件改变时 */
  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    const { fileList, file } = info;
    if (file.status === 'error') {
      ProNotification.warning({
        message: `${file.name}上传失败`,
      });
    }
    const newFileList = fileList
      .filter(item => item.status !== 'error')
      .map(item => {
        const { thumbUrl, ...restItem } = item;
        if (item.status !== 'done') {
          return restItem;
        }
        return item;
      });
    if (file.status === 'done' && showUploadList === false) {
      showUploadList !== false ? onChange?.(newFileList) : onChange?.(file);
      setCurrentFileList(newFileList);
      return false;
    }
    if (showUploadList !== false)
      onChange?.(
        Array.isArray(newFileList) && newFileList.length
          ? newFileList
          : undefined,
      );
    setCurrentFileList(
      Array.isArray(newFileList) && newFileList.length
        ? newFileList
        : undefined,
    );
  };
  /** 点击文件预览 */
  const handlePreview = (file: UploadFile<any>) => {
    Helper.previewImage({
      getPopupContainer,
      current: file.url,
      images: currentFileList?.map(item => item.url || ''),
    });
    return false;
  };
  /** 操作按钮 */
  const uploadButton = disabled ? null : (
    <div>
      {uploadIcon}
      {
        size !== 'small' && <div>{uploadText}</div>
      }
    </div>
  );
  const uploadClassName = useMemo(() => {
    return classNames(className, {
      'yb-upload-small': size === 'small',
    });
  }, [size, className]);

  return (
    <ProUpload
      maxCount={maxCount}
      className={uploadClassName}
      showUploadList={showUploadList}
      multiple={true}
      fileList={currentFileList}
      disabled={disabled}
      {...restProps}
      onChange={handleChange}
      beforeUpload={handleBeforeUpload}
      data={handleExtraData}
      action={OSSData?.host}
      onPreview={handlePreview}
    >
      {(maxCount && currentFileList && currentFileList.length >= maxCount) ||
      disabled
        ? null
        : uploadButton}
    </ProUpload>
  );
};

AliUploadImages.defaultProps = {
  listType: 'picture-card',
  uploadText: '点击上传',
  uploadIcon: <UploadOutlined />,
  name: 'file',
  multiple: true,
};

export default AliUploadImages;
